
import {Component, Prop, Vue, Watch} from 'vue-property-decorator';
import {ToyDocument} from '@/models/tobject.model';
import SbDetailsParameter from '@/components/stead/workspaces/stepbuilder/sbDetailsParameter.vue';
import {InstructionDocument} from '@/models/instruction.model';
import {ParamDescDocument, ParamValueDocument} from '@/models/parameter.model';
import {payloadStep} from '@/store/stead/step.store';
import {JisonParser} from '@/shared/jison/jison.parser';
import SbDetailsVariables from '@/components/stead/workspaces/stepbuilder/sbRunnerFormVariables.vue';
import {lightToHeavy, StepHeavyDocument, StepLightDocument} from "@/models/step.model";
import SbToyScreen from "@/components/stead/workspaces/stepbuilder/sbToyScreen.vue";

@Component({
  components: {SbToyScreen, SbDetailsVariables, SbDetailsParameter}
})
export default class SbDetails extends Vue {
  // Les props
  @Prop() private tcid!: string
  @Prop() private lightStep!: StepLightDocument | null
  @Prop() private pos!: number

  // Les propriétés
  private step: StepHeavyDocument | null = null
  private modeEdit: boolean = false
  private instrucId: string = ''
  private updatedParams: ParamValueDocument[] = []
  private msgSnackbar: string = ''
  private showSnackbar: boolean = false
  private saving: boolean = false
  private initialStep: StepHeavyDocument | null = null

  // Les propriétés calculées
  private get TObject() {
    return this.step ? this.step.tobject : null
  }

  private get aToy(): ToyDocument | null {
    return this.TObject && this.TObject.kind === 'Toy' ? (this.TObject as unknown as ToyDocument) : null
  }

  private get instructions(): InstructionDocument[] {
    return (this.TObject && this.TObject.type) ? this.TObject.type.instructions : []
  }

  private get instrucDoc(): string {
    const instruc = this.instructions.find((i: InstructionDocument) => i._id === this.instrucId)
    return instruc && instruc.documentation ? instruc.documentation : ''
  }

  private get instrucParams(): ParamValueDocument[] {
    const stepParams = this.step && this.step.parameters ? this.step.parameters : []
    const instruc = this.instructions.find((i: InstructionDocument) => i._id === this.instrucId)
    if (instruc) {
      instruc.parameters.sort((p1, p2) => p1.pos - p2.pos)
      return instruc.parameters.map<ParamValueDocument>((descId: ParamDescDocument) => {
        const stepParam = stepParams.find(pv => pv.descId === descId._id)
        return {
          descId,
          value: stepParam ? stepParam.value : ''
        }
      })
    } else {
      return []
    }
  }

  private get isValidStep(): boolean {
    if (!this.TObject) {
      return false
    } else if (this.TObject.type.kind === 'ttoy' && this.aToy) {
      return !!this.aToy.screen
    }
    return true
  }

  private get hasOldParameters() {
    if (this.step && this.step.parameters) {
      for (const pvd of this.step.parameters) {
        if (!pvd.descId) {
          return true
        }
      }
    }
    return false
  }

  private get hasNewParameters() {
    const stepParams = this.step && this.step.parameters ? this.step.parameters : []
    const instruc = this.instructions.find((i) => i._id === this.instrucId)
    if (instruc) {
      for (const descId of instruc.parameters) {
        const idx = stepParams.findIndex((pvd) => pvd.descId === descId._id)
        if (idx < 0 && descId.required) {
          return true
        }
      }
    }
    return false
  }

  private get alertBrokenInstruc() {
    if (this.modeEdit) {
      return null
    }
    if (this.instrucId.length) {
      const stepinstruc = this.instructions.find(i => this.step ? (i._id === this.step.instruc) : false)
      const instrucName = stepinstruc ? stepinstruc.name : ''
      const objTypeName = this.step && this.step.tobject && this.step.tobject.type ? this.step.tobject.type.typename : ''
      const instruc = this.instructions.find((i) => i._id === this.instrucId)
      return !instruc ? `The instruction <b>${instrucName}</b> has been removed from its object type '<b>${objTypeName}</b>'` : null
    } else {
      return 'Instruction not found because it has been deleted'
    }
  }

  private get infoBrokenInstruc() {
    if (this.instrucId.length === 0) {
      return 'You must change the instruction or delete this step'
    } else {
      const objTypeName = this.step && this.step.tobject && this.step.tobject.type ? this.step.tobject.type.typename : ''
      return `In order to repair you can : <ul>
<li>Change the instruction</li>
<li>Go to the forge to reassign the instruction to the object type <b>${objTypeName}</b></li>
<li>Delete this step</li></ul>`
    }
  }

  // Les hooks
  public mounted() {
    this.loadStepData()
  }

  // Les méthodes surveillées
  @Watch('lightStep')
  private onStepChange() {
    this.loadStepData()
  }

  @Watch('instrucId')
  private onChangeInstruction() {
    this.updatedParams = [...this.instrucParams]
  }

  @Watch('pos')
  private onChangePos() {
    this.modeEdit = false
  }

  // Les méthodes d'instance
  private loadStepData() {
    this.step = lightToHeavy(this.lightStep)
    this.initData()
  }

  /**
   * Data initialization
   */
  private initData() {
    if (this.step) {
      this.instrucId = this.step.instruc
      this.updatedParams = [...this.instrucParams]
      this.initialStep = this.step
    } else {
      this.instrucId = ''
      this.updatedParams = []
      this.initialStep = null
    }
  }

  private async onSaveChanges() {
    // Checks whether all the required parameters have been entered
    if (this.isEligible()) {
      if (this.step && this.step.tobject) {
        const payload = {
          tcid: this.tcid,
          pos: this.pos,
          steps: {
            instruc: this.instrucId,
            tobject: this.step.tobject._id,
            parameters: this.updatedParams.map((pvd) => {
              return {
                descId: pvd.descId._id,
                value: pvd.value
              }
            })
          }
        } as payloadStep
        this.saving = true
        await this.$store.dispatch('step/updateStep', payload)
        this.saving = false
        this.$emit('updated')
      }
      this.modeEdit = false
    }
  }

  private onCancelChanges() {
    this.step = this.initialStep
    this.initialStep = null
    this.modeEdit = false
  }

  /**
   * Checks if all the required parameters are filled and if all the parameters have a correct syntax
   */
  private isEligible(): boolean {
    let flag: boolean = false
    this.updatedParams.forEach((pvd: ParamValueDocument) => {
      if (pvd.value.length === 0 && pvd.descId.required) {
        flag = true
      }
    })
    if (flag) {
      this.msgSnackbar = 'Required parameters must be entered before saving changes'
      this.showSnackbar = true
      return false
    }
    this.updatedParams.forEach((pvd: ParamValueDocument) => {
      if (pvd.value.length > 0) {
        const parser = JisonParser.builder(pvd.descId)
        if (parser && typeof parser.exec(pvd.value) === 'string') {
          flag = true
          // console.log(`syntaxe error sur ${pvd.descId.label} = ${pvd.value}`)
        }
      }
    })
    if (flag) {
      this.msgSnackbar = 'Syntax error on one or more parameters'
      this.showSnackbar = true
      return false
    }
    return true
  }

  private alertParamText(type: 'old' | 'new') {
    const instruc = this.instructions.find(i => this.step ? (i._id === this.step.instruc) : false)
    const basetext = type === 'old'
        ? 'Parameters have been removed from'
        : type === 'new' ? 'Required parameters have been added in' : null
    return instruc && basetext ? basetext + ` instruction <b>${instruc.name}</b>` : ''
  }


  // Les méthodes statiques
}

